import { Card, Link, Typography } from '@material-ui/core'
import { cloneElement } from 'react'
import {
  ArrayField,
  Datagrid,
  DateField,
  EditButton,
  FunctionField,
  Labeled,
  SimpleShowLayout,
  TextField,
  Toolbar,
  TopToolbar,
  useRedirect,
  useShowController,
} from 'react-admin'
import { usePageTitle } from 'shared/components/PageTitle/PageTitle'
import { SidebarOpenerLink } from 'shared/components/Sidebar/Sidebar.styles'

import {
  DESTINATION,
  FSM_TRANSITION_RECORD_TYPE,
  JobStatusDetail,
  LABEL_DROPOFF_DATE,
  LOAD_STOP_WINDOW_TYPES,
  StatementType,
} from '../../helpers/constants'
import { getLoadStop, getPaymentPolicyLinkForId } from '../../helpers/utils'
import { useStatement } from '../../hooks/useStatement'
import { hasPermission, Permissions } from '../../Permissions'
import Aside from '../Aside/aside'
import { CorrectionFinalizeButton } from '../Common/Statements'
import { GenericFormField, LabeledTypographyField, LocalizedStopDateTimeField } from '../Fields'
import { FSMButtonToolbar } from '../FSM/Buttons/FSMTransitionButton'
import { TeamLink } from '../Teams/TeamLink'

const StatementShowToolbar = (props: any) => {
  const { record, permissions } = props

  usePageTitle('Statement')

  if (!record || !record.job) {
    return null
  }
  let toolbar
  switch (record.type) {
    case StatementType.Job:
      const job = record.job
      let actionPermission =
        job.status_detail === JobStatusDetail.PaymentRequired
          ? Permissions.IssuePaymentStatement
          : Permissions.ReimburseStatement
      toolbar = (
        <FSMButtonToolbar
          classes={null}
          record={job}
          recordType={FSM_TRANSITION_RECORD_TYPE.job}
          includeCancel={false}
          isHidden={
            !hasPermission(permissions, actionPermission) ||
            ![JobStatusDetail.PaymentRequired, JobStatusDetail.PaymentIssued].includes(job.status_detail)
          }
        />
      )
      break
    case StatementType.JobCorrection:
      toolbar = (
        <Toolbar {...props}>
          <CorrectionFinalizeButton permissions={permissions} record={record} />;
        </Toolbar>
      )
      break
    default:
      toolbar = null
  }
  return toolbar
}

const StatementShowActions = ({ basePath, data, permissions }: any) => {
  if (!data || !permissions) {
    return null
  }
  const canChangeStatement = hasPermission(permissions, Permissions.ChangeStatement)
  const isEditable = data.is_editable
  return (
    <TopToolbar>
      {canChangeStatement && isEditable && <EditButton basePath={basePath} record={data} />}
    </TopToolbar>
  )
}

const JobSection = ({ record }: any) => {
  const redirect = useRedirect()
  const jobRedirectPath = `/jobs/${record.id}/show/statements`
  // Protect against redirect with uuid as record
  if (typeof record !== 'object') {
    return null
  }
  const destStop = getLoadStop(record.load, DESTINATION)
  return (
    <>
      <Labeled label="Job ID">
        <Typography variant="body1">
          <Link
            onClick={() => {
              redirect(jobRedirectPath)
            }}
            style={{ cursor: 'pointer' }}
          >
            {record.human_id}
          </Link>
        </Typography>
      </Labeled>
      <LabeledTypographyField label="Load source" variant="body2" value={record.load.source} />
      <LabeledTypographyField label="Load source id" variant="body2" value={record.load.source_human_id} />
      <LabeledTypographyField label="Job status detail" variant="body2" value={record.status_detail} />
      {destStop && (
        <Labeled label={LABEL_DROPOFF_DATE}>
          <LocalizedStopDateTimeField record={destStop} source={LOAD_STOP_WINDOW_TYPES.start} />
        </Labeled>
      )}
    </>
  )
}

const CustomStatementShow = (props: any) => {
  const { basePath, loaded, record, resource, version } = useShowController(props)

  const { permissions } = props
  if (!permissions || !record) {
    return null
  }

  const job = record.job
  const pageTitle = record.type === StatementType.Job ? 'Job statement' : 'Correction statement'
  const jobSection = job ? <JobSection record={job} /> : null

  return loaded ? (
    <>
      <StatementShowActions permissions={permissions} basePath={basePath} data={record} />
      <Typography variant="h4" component="span">
        {pageTitle}
      </Typography>
      {jobSection}
      <Card style={{ marginTop: '20px' }}>
        {cloneElement(props.children, {
          basePath,
          record,
          resource,
          version,
        })}
      </Card>
    </>
  ) : null
}

const StatementShow = ({ permissions, ...props }: any) => {
  const { statement } = useStatement(props.id, { enabled: !!props.id })
  const { record } = useShowController(props)

  return (
    <>
      <CustomStatementShow permissions={permissions} {...props}>
        <SimpleShowLayout>
          <TextField source="type" />
          <TextField source="status" />
          <ArrayField source="line_items">
            <Datagrid>
              <TextField source="amount" />
              <TextField source="type" />
              <TextField source="description" />
            </Datagrid>
          </ArrayField>
          <TextField source="subtotal" />
          <FunctionField
            label="Fee items"
            render={(record: any) => {
              if (record.fee_items) {
                return record.fee_items.length === 0 ? (
                  <GenericFormField label="0.00" />
                ) : (
                  <ArrayField record={record} source="fee_items">
                    <Datagrid>
                      <TextField source="amount" />
                      <FunctionField
                        label="Description"
                        render={(record: any) => {
                          if (record.optional_policy_id) {
                            const paymentLink = getPaymentPolicyLinkForId(record.optional_policy_id)
                            return <a href={paymentLink}>{record.description}</a>
                          } else {
                            return <TextField source="description" />
                          }
                        }}
                      />
                      <TextField source="type" />
                    </Datagrid>
                  </ArrayField>
                )
              }
              return null
            }}
          />
          <TextField source="lease_fee_percent" />
          <TextField source="lease_fee" />
          <TextField source="service_fee_percent" />
          <TextField source="service_fee" />
          <TextField source="dispatch_fee_percent" />
          <TextField source="dispatch_fee" />
          <FunctionField
            label="Cash advance payments"
            render={(record: any) => {
              if (record.cash_advance_payments) {
                return record.cash_advance_payments.length === 0 ? (
                  <GenericFormField label="0.00" />
                ) : (
                  <ArrayField record={record} source="cash_advance_payments">
                    <Datagrid>
                      <FunctionField
                        label="Total"
                        render={(record: any) => {
                          let total = parseFloat(record.amount) + parseFloat(record.fee)
                          return <GenericFormField label={total.toFixed(2)} />
                        }}
                      />
                      <TextField source="amount" />
                      <TextField source="fee" />
                      <TextField source="status" />
                    </Datagrid>
                  </ArrayField>
                )
              }
              return null
            }}
          />
          <TextField source="total" />
          <DateField source="date_processed" options={{ timeZoneName: 'short' }} />
          <DateField source="date_updated" options={{ timeZoneName: 'short' }} />
          <DateField source="date_created" options={{ timeZoneName: 'short' }} />
          <TextField source="description" />
          <FunctionField
            label="Team"
            render={(record: any) => (
              <TeamLink
                team={{
                  // This is a workaround for the fact that team serialization is
                  // inconsistent between the put and post responses for statements.
                  id:
                    statement?.team?.id ??
                    (typeof record?.team === 'object' ? record?.team.id : record?.team),
                  name: statement?.team?.name,
                  productSubscriptionName: statement?.team?.formatted_subscription_name,
                }}
              >
                <SidebarOpenerLink>
                  {typeof record?.team === 'object' ? record?.team?.name : statement?.team?.name}
                </SidebarOpenerLink>
              </TeamLink>
            )}
          />
          <StatementShowToolbar permissions={permissions} />
        </SimpleShowLayout>
      </CustomStatementShow>
      <Aside record={record} resource="statements" />
    </>
  )
}

// TODO: Remove default export
// eslint-disable-next-line import/no-default-export
// @ts-ignore
export default StatementShow
